<template>
  <div class="comment-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div>
        <div class="page-title">评论管理</div>
        <div class="page-desc">查看和管理用户评论，及时回复用户反馈</div>
      </div>
    </div>

    <!-- 搜索区域 -->
    <el-card class="search-card" shadow="never">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item>
          <el-input v-model="searchForm.content" placeholder="请输入评论内容" clearable prefix-icon="el-icon-search"
            style="width: 280px" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            查询
          </el-button>

          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 列表 -->
    <el-card class="table-card" shadow="never">
      <div class="table-header">
        <div class="table-title">评论列表</div>
        <div class="table-total">
          共 {{ total }} 条评论
        </div>
      </div>

      <el-table :data="tableData" v-loading="loading" border class="comment-table">
        <!-- 用户 -->
        <el-table-column label="用户" width="180">
          <template slot-scope="{ row }">
            <div class="user-info">
              <el-avatar :size="38" :src="row.avatar">
                {{ row.nickname ? row.nickname.charAt(0) : 'U' }}
              </el-avatar>

              <div class="user-detail">
                <div class="user-name">
                  {{ row.nickname || '-' }}
                </div>

                <div class="user-id">
                  ID：{{ row.userId || '-' }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- 评论内容 -->
        <el-table-column label="评论内容" min-width="380">
          <template slot-scope="{ row }">
            <div class="comment-content">
              <div class="comment-text">
                {{ row.content || '-' }}
              </div>

              <div v-if="row.sourceTitle" class="comment-source">
                所属内容：
                <span>{{ row.sourceTitle }}</span>
              </div>

              <div v-if="row.replyContent" class="reply-preview">
                <span class="reply-label">管理员回复：</span>
                {{ row.replyContent }}
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- 时间 -->
        <el-table-column label="评论时间" width="170" prop="add_time" />

        <!-- 状态 -->
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.replyContent" type="success" size="small">
              已回复
            </el-tag>

            <el-tag v-else type="warning" size="small">
              待回复
            </el-tag>
          </template>
        </el-table-column>

        <!-- 操作 -->
        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="handleReply(row)">
              {{ row.replyContent ? '查看回复' : '回复' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination background layout="total, sizes, prev, pager, next, jumper" :total="total"
          :page-size="searchForm.limit" :current-page="searchForm.page" @current-change="handlePageChange"
          @size-change="handleSizeChange" />
      </div>
    </el-card>

    <!-- 回复弹窗 -->
    <el-dialog title="回复评论" :visible.sync="replyDialogVisible" width="560px" custom-class="reply-dialog"
      @close="resetReply">
      <div v-if="currentComment" class="reply-dialog-content">
        <!-- 用户信息 -->
        <div class="dialog-user">
          <el-avatar :size="42" :src="currentComment.avatar">
            {{
              currentComment.nickname
                ? currentComment.nickname.charAt(0)
                : 'U'
            }}
          </el-avatar>

          <div>
            <div class="dialog-user-name">
              {{ currentComment.nickname || '-' }}
            </div>

            <div class="dialog-time">
              {{ currentComment.createTime || '-' }}
            </div>
          </div>
        </div>

        <!-- 原评论 -->
        <div class="original-comment">
          <div class="section-label">用户评论</div>

          <div class="original-content">
            {{ currentComment.content || '-' }}
          </div>
        </div>

        <!-- 回复输入 -->
        <el-form ref="replyForm" :model="replyForm" :rules="replyRules">
          <el-form-item label="回复内容" prop="content">
            <el-input v-model="replyForm.content" type="textarea" :rows="5" maxlength="500" show-word-limit
              placeholder="请输入回复内容" />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer">
        <el-button @click="replyDialogVisible = false">
          取消
        </el-button>

        <el-button type="primary" :loading="replyLoading" @click="submitReply">
          提交回复
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { orderCommentCommentList, orderCommentReplyAdd } from '@/api/xgcApi'

export default {
  name: 'CommentManage',

  data() {
    return {
      // 列表加载
      loading: false,

      // 回复加载
      replyLoading: false,

      // 总条数
      total: 0,

      // 查询参数
      searchForm: {
        page: 1,
        limit: 10,
        content: ''
      },

      // 表格数据
      tableData: [],

      // 回复弹窗
      replyDialogVisible: false,

      // 当前评论
      currentComment: null,

      // 回复表单
      replyForm: {
        content: ''
      },

      // 回复校验
      replyRules: {
        content: [
          {
            required: true,
            message: '请输入回复内容',
            trigger: 'blur'
          }
        ]
      }
    }
  },

  created() {
    this.getCommentList()
  },

  methods: {
    /**
     * 获取评论列表
     */
    getCommentList() {
      this.loading = true

      orderCommentCommentList({
        page: this.searchForm.page,
        limit: this.searchForm.limit,
        content: this.searchForm.content
      })
        .then(res => {
          /**
           * 根据实际接口返回结构处理
           *
           * 当前兼容以下几种常见格式：
           * res.data.records
           * res.data.rows
           * res.data.list
           * res.rows
           */

          const data = res.data || {}

          this.tableData =
            data.records ||
            data.rows ||
            data.list ||
            res.rows ||
            []

          this.total =
            data.total ||
            res.total ||
            0
        })
        .catch(() => {
          this.tableData = []
          this.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * 查询
     */
    handleSearch() {
      this.searchForm.page = 1
      this.getCommentList()
    },

    /**
     * 重置
     */
    handleReset() {
      this.searchForm.content = ''
      this.searchForm.page = 1
      this.getCommentList()
    },

    /**
     * 页码改变
     */
    handlePageChange(page) {
      this.searchForm.page = page
      this.getCommentList()
    },

    /**
     * 每页条数改变
     */
    handleSizeChange(size) {
      this.searchForm.limit = size
      this.searchForm.page = 1
      this.getCommentList()
    },

    /**
     * 打开回复弹窗
     */
    handleReply(row) {
      this.currentComment = row

      this.replyForm.content = row.replyContent || ''

      this.replyDialogVisible = true
    },

    /**
     * 重置回复表单
     */
    resetReply() {
      this.currentComment = null

      this.replyForm.content = ''

      if (this.$refs.replyForm) {
        this.$refs.replyForm.resetFields()
      }
    },

    /**
     * 提交回复
     *
     * 这里等待你提供真实的回复接口
     */
    submitReply() {
      this.$refs.replyForm.validate(valid => {
        if (!valid) return

        this.replyLoading = true

        // TODO: 这里替换成真实的回复接口
        // 例如：
        orderCommentReplyAdd({
          comment_id: this.currentComment.id,
          content: this.replyForm.content
        }).then(res => {
          setTimeout(() => {
            this.$message.success('回复成功')
            this.replyLoading = false
            this.replyDialogVisible = false
            // 重新刷新列表
            this.getCommentList()
          }, 500)
        })


      })
    }
  }
}
</script>

<style scoped>
.comment-page {
  min-height: 100%;
  padding: 24px;
  background: #f5f7fa;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #303133;
  line-height: 32px;
}

.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}

/* 搜索卡片 */
.search-card {
  margin-bottom: 16px;
  border-radius: 6px;
}

.search-form {
  margin-bottom: -18px;
}

/* 表格卡片 */
.table-card {
  border-radius: 6px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.table-total {
  font-size: 13px;
  color: #909399;
}

/* 用户信息 */
.user-info {
  display: flex;
  align-items: center;
}

.user-detail {
  margin-left: 10px;
  overflow: hidden;
}

.user-name {
  font-size: 14px;
  color: #303133;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-id {
  font-size: 12px;
  color: #a8abb2;
}

/* 评论内容 */
.comment-content {
  padding: 4px 0;
}

.comment-text {
  color: #303133;
  line-height: 22px;
  font-size: 14px;
  word-break: break-all;
}

.comment-source {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.comment-source span {
  color: #409eff;
}

/* 回复预览 */
.reply-preview {
  margin-top: 10px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}

.reply-label {
  color: #67c23a;
  font-weight: 500;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 弹窗 */
.reply-dialog-content {
  padding: 0 4px;
}

.dialog-user {
  display: flex;
  align-items: center;
  margin-bottom: 22px;
}

.dialog-user-name {
  margin-left: 12px;
  font-size: 15px;
  color: #303133;
  font-weight: 500;
}

.dialog-time {
  margin-left: 12px;
  margin-top: 5px;
  color: #909399;
  font-size: 12px;
}

.original-comment {
  margin-bottom: 22px;
}

.section-label {
  margin-bottom: 10px;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.original-content {
  padding: 14px 16px;
  background: #f5f7fa;
  border-radius: 5px;
  color: #606266;
  line-height: 24px;
  font-size: 14px;
}
</style>